import isEmpty from 'lodash/isEmpty';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { createFilterOptions, FilterOptionsState } from '@material-ui/lab';

import { MenuItemIndividualItem } from '../../../../../atomic/molecules/Dropdown_V2/interfaces';
import { AutocompleteDropdownItem } from '../../../../../atomic/organism/Autocomplete/interfaces';
import {
  useGetMembersFromCriteriaMutation,
  useMembersSearch,
} from '../../../../../hooks/useMembersSearch';
import { FlowBuilderBlockTypes } from '../../../../../interfaces/Flow/Builder';
import useFlowBuilderStore from '../../../../../stores/flowBuilderStore';
import {
  flowIdSelector,
  setSpecificBlockDataSelector,
} from '../../../../../stores/flowBuilderStore/selectors';
import {
  mapMemberDetailsToAutoCompleteDropdownData,
  mapParticipationAndVisibilityDataToSimpleRules,
} from '../../../../../Utils/flows';
import { mapCriteriaResponseToBlockData } from '../../../../../Utils/flows/builder/utils';
import { mapRulesFromCriteriaGroupsAndReturnDepartmentsOnly } from '../../../../../Utils/flows/criteria';
import {
  getShareSheetCardDataFromAutoCompleteOption,
  getUpdatedSelectedItemsBasedOnDropdownSelection,
  serializeSimpleShareSheetRulesForAPIPayload,
} from '../../../../../Utils/flows/sharesheet';
import { isValidEmail } from '../../../../../Utils/validation';
import {
  SimplifiedShareSheetCardData,
  SimplifiedShareSheetDataTypes,
  VisibilityTypes,
} from '../../types';
import {
  animationEffect,
  animationOptions,
  bulkMemberActions,
  canPostAndViewPostsSetting,
  DepartmentAvatar,
  getInviteUserCard,
  noResultsFoundCard,
  ShareSheetMemberActions,
} from '../data';
import { useFetchFlowDetailsQuery } from '../../../../../queries/Flows/Feed';
import { useProfileInfoFetchQuery } from '../../../../../queries/Profile';
import { MemberState } from '../../../../../interfaces/user';
import { IMemberDTO, MemberRole } from '../../../../../interfaces/member';
import { useGetAllDepartmentsQuery } from '../../../../../queries/FlowBuilder/Participants';
import { useFetchMembersBasedOnFlowCriteria } from '../useFetchMembersBasedOnFlowCriteria';
import { useImmer } from 'use-immer';

export const useFlowsSimpleShareSheet = ({
  handleOnModifiedChanges,
  handleMembersToBeInvited,
}: {
  handleOnModifiedChanges: (hasDataChanged: boolean) => void;
  handleMembersToBeInvited: (members: string[]) => void;
}) => {
  const [textBoxValue, setTextBoxValue] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<
    SimplifiedShareSheetCardData[]
  >([]);

  const [value, setValue] = useState<AutocompleteDropdownItem<string> | null>(
    null,
  );
  const [isDepartmentSelectionInProgress, setIsDepartmentSelectionInProgress] =
    useState<boolean>(false);

  const flowId = useFlowBuilderStore(flowIdSelector);

  const shouldShowTooltipOnUserCard = useMemo(() => {
    return selectedItems.some(
      (i) => i.type === 'everyone' || i.type === 'department',
    );
  }, [selectedItems]);

  const {
    models: {
      hasMoreMembers,
      searchedMembers,
      isLoading: isMembersLoading,
      isFetching: isMembersFetching,
      canEmailInvite,
    },
    operations: { fetchMoreMembers, onChange },
  } = useMembersSearch(true, 100);

  const { data: flowDetails } = useFetchFlowDetailsQuery(
    flowId || '',
    'builder',
  );

  const { data: profileInfo } = useProfileInfoFetchQuery();

  const {
    isParticipantsCriteriaLoading,
    participantsSelected,
    isEveryoneCriteriaBeingFetched,
    totalParticipantsCount,
    isViewerCriteriaLoading,
    membersWithVisibility,
    participantsCriteria,
    visibilityCriteria,
    membersCountForEveryoneCriteria,
  } = useFetchMembersBasedOnFlowCriteria({
    enableFetch: isEmpty(selectedItems),
    isMembersOnly: true,
  });

  const { mutateAsync: getDepartmentFromMemberCriteriaMutation } =
    useGetMembersFromCriteriaMutation();

  const { data: departments, isLoading: isDepartmentsLoading } =
    useGetAllDepartmentsQuery();

  const [departmentCountMap, setDepartmentCountMap] = useImmer<
    Record<string, number>
  >({});

  const memberOptions = useMemo(() => {
    if (searchedMembers) {
      if (canEmailInvite) {
        return [
          {
            firstName: '',
            lastName: '',
            memberState: MemberState.PENDING,
            email: textBoxValue,
            role: [MemberRole.Employee],
            username: textBoxValue,
            memberID: 'invite-user-card',
          },
        ] as IMemberDTO[];
      }
      return searchedMembers;
    }
    return [];
  }, [canEmailInvite, searchedMembers, textBoxValue]);

  const setSpecificBlockData = useFlowBuilderStore(
    setSpecificBlockDataSelector,
  );

  useEffect(() => {
    if (
      !isParticipantsCriteriaLoading &&
      !isEmpty(participantsCriteria) &&
      !isViewerCriteriaLoading &&
      !isEmpty(visibilityCriteria)
    ) {
      const nonMemberItems = [
        ...mapRulesFromCriteriaGroupsAndReturnDepartmentsOnly(
          participantsCriteria,
          visibilityCriteria,
          totalParticipantsCount,
        ),
      ];
      if (
        (participantsSelected ||
          membersWithVisibility ||
          nonMemberItems.length > 0) &&
        selectedItems.length === 0
      ) {
        const items = [
          ...nonMemberItems,
          ...mapParticipationAndVisibilityDataToSimpleRules({
            participants: participantsSelected || [],
            membersWithVisibility: membersWithVisibility || [],
            flowOwner: flowDetails?.data?.owner,
          }),
        ];
        setSelectedItems(items);
      }
    }
  }, [
    isViewerCriteriaLoading,
    isParticipantsCriteriaLoading,
    membersWithVisibility,
    participantsSelected,
    flowDetails?.data?.owner,
    participantsCriteria,
    selectedItems,
    totalParticipantsCount,
    visibilityCriteria,
  ]);

  const departmentOptions: AutocompleteDropdownItem<string>[] = useMemo(() => {
    if (departments) {
      return departments.data.map((department: string) => {
        return {
          title: department,
          id: department,
        };
      });
    }
    return [];
  }, [departments]);

  useEffect(() => {
    onChange(textBoxValue);
  }, [onChange, textBoxValue]);

  useEffect(() => {
    const membersToBeInvited = selectedItems
      .filter((i) => !isEmpty(i.emailToInvite))
      .map((i) => i.emailToInvite || '');

    const participants = selectedItems.filter(
      (i) =>
        i.setting?.id === ShareSheetMemberActions.canOnlyPost ||
        i.setting?.id === ShareSheetMemberActions.canPostAndViewPosts,
    );
    const viewers = selectedItems.filter(
      (i) =>
        i.setting?.id === ShareSheetMemberActions.canOnlyViewPosts ||
        i.setting?.id === ShareSheetMemberActions.canPostAndViewPosts,
    );
    const serializedParticipationCriteria = mapCriteriaResponseToBlockData(
      serializeSimpleShareSheetRulesForAPIPayload(participants),
    );

    const serializedViewingCriteria = mapCriteriaResponseToBlockData(
      serializeSimpleShareSheetRulesForAPIPayload(viewers),
    );
    if (membersToBeInvited.length > 0) {
      handleMembersToBeInvited(membersToBeInvited);
    }
    if (participants.length > 0) {
      setSpecificBlockData(FlowBuilderBlockTypes.PARTICIPANTS, {
        participantsCriteria: serializedParticipationCriteria,
      });
      handleOnModifiedChanges(true);
    }
    if (viewers.length > 0) {
      setSpecificBlockData(FlowBuilderBlockTypes.VISIBILITY, {
        criteriaGroups: serializedViewingCriteria || undefined,
        everyone: false,
        onlyParticipants: false,
        onlyOwners: false,
        custom: true,
        type: VisibilityTypes.CUSTOM,
      });
      handleOnModifiedChanges(true);
    }
  }, [
    handleMembersToBeInvited,
    handleOnModifiedChanges,
    selectedItems,
    setSpecificBlockData,
  ]);

  const displayOptions = useMemo(() => {
    const individualMembers =
      mapMemberDetailsToAutoCompleteDropdownData(memberOptions);

    return [bulkMemberActions, ...(individualMembers || [])];
  }, [memberOptions]);

  const resetAutoCompleteDropdown = useCallback(
    (inputRef: MutableRefObject<HTMLInputElement | null>) => {
      if (inputRef?.current) {
        inputRef.current.blur();
        inputRef.current.value = '';
      }
    },
    [],
  );

  const scrollToOption = useCallback(
    (optionId: string, inputRef: MutableRefObject<HTMLInputElement | null>) => {
      const element = document.getElementById(`displayCard-${optionId}`);
      if (element) {
        element.scrollIntoView({
          block: 'center',
          inline: 'center',
          behavior: 'smooth',
        });
        element.animate(animationEffect, animationOptions);
        setTimeout(() => {
          resetAutoCompleteDropdown(inputRef);
          setTextBoxValue('');
        }, 100);
      }
    },
    [resetAutoCompleteDropdown],
  );

  const handleAutoCompleteOptionSelection = useCallback(
    (
      selectedAutoCompleteOption: AutocompleteDropdownItem<string>,
      inputRef: MutableRefObject<HTMLInputElement | null>,
    ) => {
      const isExistingInSelectedItems =
        selectedItems.findIndex((option) =>
          selectedAutoCompleteOption?.id === 'invite-user-card'
            ? selectedAutoCompleteOption?.emailToInvite === option?.id
            : option.id === selectedAutoCompleteOption?.id,
        ) > -1;
      if (selectedAutoCompleteOption.id === 'bulk-member-actions') return;
      if (isExistingInSelectedItems) {
        scrollToOption(
          selectedAutoCompleteOption?.id === 'invite-user-card' &&
            selectedAutoCompleteOption?.emailToInvite
            ? selectedAutoCompleteOption?.emailToInvite
            : selectedAutoCompleteOption.id,
          inputRef,
        );
      } else {
        const modifiedSelectedItems = [
          getShareSheetCardDataFromAutoCompleteOption({
            option: selectedAutoCompleteOption,
            setting: canPostAndViewPostsSetting,
            type: SimplifiedShareSheetDataTypes.Member,
          }),
          ...selectedItems,
        ];
        setSelectedItems(modifiedSelectedItems);
        setValue(selectedAutoCompleteOption);
      }
    },
    [scrollToOption, selectedItems],
  );

  const handleBulkMemberActionSelection = useCallback(
    (actionType: 'department' | 'everyone') => {
      let currentItem: SimplifiedShareSheetCardData | undefined;
      if (actionType === 'department') {
        setIsDepartmentSelectionInProgress(true);
      } else if (actionType === 'everyone') {
        currentItem = {
          id: 'everyone',
          title: 'Everyone in this workspace',
          setting: canPostAndViewPostsSetting,
          icon: 'everyone',
          email: `${totalParticipantsCount} members`,
          type: SimplifiedShareSheetDataTypes.Everyone,
        };
      }
      if (currentItem) {
        const modifiedSelectedItems = [
          currentItem,
          ...selectedItems.map((item) => ({
            ...item,
            type: SimplifiedShareSheetDataTypes.Member,
          })),
        ];
        setSelectedItems(modifiedSelectedItems);
      }
    },
    [selectedItems, totalParticipantsCount],
  );

  const filterOptions = (
    options: AutocompleteDropdownItem<string, undefined>[],
    state: FilterOptionsState<AutocompleteDropdownItem<string, undefined>>,
  ) => {
    const filter =
      createFilterOptions<AutocompleteDropdownItem<string, undefined>>();
    const filtered = filter(options, state);
    if (state.inputValue !== '' && filtered.length === 0 && !isMembersLoading) {
      const emptyState = isValidEmail(state.inputValue)
        ? getInviteUserCard(state.inputValue)
        : noResultsFoundCard;
      filtered.push(emptyState);
    }
    return filtered;
  };

  const onOptionsScroll = () => {
    if (hasMoreMembers && !isMembersFetching) {
      fetchMoreMembers();
    }
  };

  const changeUserSettings = (
    setting: MenuItemIndividualItem,
    userId: string,
  ) => {
    const modifiedSelectedItems =
      getUpdatedSelectedItemsBasedOnDropdownSelection({
        currentUserId: userId,
        setting,
        selectedItems,
      });
    setSelectedItems(modifiedSelectedItems);
  };

  const resetSelectedItems = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const isEveryoneSelected = useMemo(() => {
    return selectedItems.some((item) => item.id === 'everyone');
  }, [selectedItems]);

  const onDepartmentSelect = useCallback(
    (
      department: AutocompleteDropdownItem<string>[],
      condition: MenuItemIndividualItem,
    ) => {
      const currentItem = department
        .filter((dept) => !selectedItems.some((i) => i.id === dept.id))
        .map((dept) => ({
          id: dept.id,
          title: `Department ${condition.value} ${dept.title}`,
          setting: canPostAndViewPostsSetting,
          email: `10 members`,
          avatar: DepartmentAvatar,
          type: SimplifiedShareSheetDataTypes.Department,
          condition: `${condition.id}`,
        }));
      if (currentItem) {
        const modifiedSelectedItems = [...currentItem, ...selectedItems];
        setSelectedItems(modifiedSelectedItems);
        setIsDepartmentSelectionInProgress(false);
      }
    },
    [selectedItems],
  );

  const onDepartmentDelete = useCallback(() => {
    setIsDepartmentSelectionInProgress(false);
  }, []);

  const getDepartmentCountForItem = useCallback(
    async (item: SimplifiedShareSheetCardData) => {
      const { data: deptQueryResponse } =
        await getDepartmentFromMemberCriteriaMutation(
          serializeSimpleShareSheetRulesForAPIPayload([item]),
        );
      setDepartmentCountMap((draft) => {
        draft[item.id] = deptQueryResponse.total;
      });
    },
    [getDepartmentFromMemberCriteriaMutation, setDepartmentCountMap],
  );

  useEffect(() => {
    selectedItems.forEach((item) => {
      getDepartmentCountForItem(item);
    });
  }, [selectedItems, getDepartmentCountForItem]);

  return {
    isMembersLoading: isMembersLoading || isMembersFetching,
    displayOptions,
    handleAutoCompleteOptionSelection,
    onOptionsScroll,
    changeUserSettings,
    filterOptions,
    value,
    textBoxValue,
    selectedItems,
    setTextBoxValue,
    isParticipantsCriteriaLoading,
    isViewerCriteriaLoading,
    isEveryoneCriteriaBeingFetched,
    resetAutoCompleteDropdown,
    currentUserId: profileInfo?.member.memberId,
    resetSelectedItems,
    canEmailInvite,
    memberOptions,
    handleBulkMemberActionSelection,
    isEveryoneSelected,
    departmentOptions,
    isDepartmentsLoading,
    isDepartmentSelectionInProgress,
    onDepartmentSelect,
    onDepartmentDelete,
    shouldShowTooltipOnUserCard,
    departmentCountMap,
    membersCountForEveryoneCriteria,
  };
};
